import type { IVariantIntervalSeries } from '../hooks/useFetchCampaignAnalyticDetailV2';
import type { AnalyticInterval } from '~/modules/analytics/types';
import type { ICampaignGroup } from '~/modules/campaign-group';
import type { IAnalyticMetric } from '~/types/metric';

import { useFetchCampaignGroupAnalyticDetailV2 } from '../hooks/useFetchCampaignGroupAnalyticDetailV2';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { shouldQueryPreviousPeriod } from '../helpers/shouldQueryPreviousPeriod';
import { DEFAULT_ANALYTIC_DATA_A, DEFAULT_ANALYTIC_DATA_B } from '../constants';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { EAnalyticMode } from '~/modules/analytics/types';
import { IVariantType } from '~/modules/campaign/types';
import { CAMPAIGN_STATUS } from '~/constants/campaign';

export interface CGAnalyticContextType {
  metrics: IAnalyticMetric[];
  metricsPrevious: IAnalyticMetric[];
  isLoadingMetric: boolean;
  isEmptyMetricData: boolean;
  analyticMode: EAnalyticMode;
  handleSetAnalyticMode: (mode: EAnalyticMode) => void;
  metricInterval: IVariantIntervalSeries[];
  interval?: AnalyticInterval;
  isLoadingMetricInterval: boolean;
}

export interface CGAnalyticProviderProps {
  children: React.ReactNode;
  campaignGroup?: ICampaignGroup;
  isQueryPreviousData: boolean;
  analyticMode?: EAnalyticMode;
  /** Listing cards render totals only; leave false there to skip the TIMESERIES query. */
  isAnalyticDetail?: boolean;
}

export const AnalyticCampaignGroupContext = createContext<CGAnalyticContextType | undefined>(
  undefined,
);

export const AnalyticCampaignGroupProvider: React.FC<CGAnalyticProviderProps> = ({
  children,
  campaignGroup,
  isQueryPreviousData,
  isAnalyticDetail = false,
  ...props
}) => {
  const { currentDates, previousDates } = useMetricsTimeFilter();
  const [analyticMode, setAnalyticMode] = useState<EAnalyticMode>(EAnalyticMode.ALL_SESSION);

  const versions = campaignGroup?.versions;
  const isDraftGroup = campaignGroup?.status === CAMPAIGN_STATUS.DRAFT;

  const effectiveIsQueryPreviousData = useMemo(
    () =>
      shouldQueryPreviousPeriod({
        isQueryPreviousData,
        versions,
        previousEndDate: previousDates.endDate,
      }),
    [isQueryPreviousData, versions, previousDates.endDate],
  );

  const detailResult = useFetchCampaignGroupAnalyticDetailV2({
    campaignGroup: isDraftGroup ? undefined : campaignGroup,
    currentDates,
    previousDates,
    isQueryPreviousData: effectiveIsQueryPreviousData,
    analyticMode,
    isFetchMetricInterval: isAnalyticDetail,
  });

  const metrics = useMemo(
    () => [
      { ...(detailResult.metricsA ?? DEFAULT_ANALYTIC_DATA_A), variantKey: IVariantType.VARIANT_A },
      { ...(detailResult.metricsB ?? DEFAULT_ANALYTIC_DATA_B), variantKey: IVariantType.VARIANT_B },
    ],
    [detailResult.metricsA, detailResult.metricsB],
  );

  const metricsPrevious = useMemo(
    () => [
      {
        ...(detailResult.metricsPreviousA ?? DEFAULT_ANALYTIC_DATA_A),
        variantKey: IVariantType.VARIANT_A,
      },
      {
        ...(detailResult.metricsPreviousB ?? DEFAULT_ANALYTIC_DATA_B),
        variantKey: IVariantType.VARIANT_B,
      },
    ],
    [detailResult.metricsPreviousA, detailResult.metricsPreviousB],
  );

  const isEmptyMetricData = useMemo(
    () =>
      (detailResult.metricsA?.totalPageViewed ?? 0) === 0 &&
      (detailResult.metricsB?.totalPageViewed ?? 0) === 0,
    [detailResult.metricsA?.totalPageViewed, detailResult.metricsB?.totalPageViewed],
  );

  const handleSetAnalyticMode = useCallback((mode: EAnalyticMode) => {
    setAnalyticMode(mode);
  }, []);

  useEffect(() => {
    if (!props.analyticMode) return;
    setAnalyticMode(props.analyticMode);
  }, [props.analyticMode]);

  const contextValue = useMemo<CGAnalyticContextType>(
    () => ({
      metrics,
      metricsPrevious,
      analyticMode,
      isEmptyMetricData,
      isLoadingMetric: detailResult.isLoadingMetric,
      handleSetAnalyticMode,
      metricInterval: detailResult.metricInterval,
      interval: detailResult.interval,
      isLoadingMetricInterval: detailResult.isLoadingMetricInterval,
    }),
    [
      metrics,
      metricsPrevious,
      analyticMode,
      isEmptyMetricData,
      handleSetAnalyticMode,
      detailResult.isLoadingMetric,
      detailResult.metricInterval,
      detailResult.interval,
      detailResult.isLoadingMetricInterval,
    ],
  );

  return (
    <AnalyticCampaignGroupContext.Provider value={contextValue}>
      {children}
    </AnalyticCampaignGroupContext.Provider>
  );
};

export const useAnalyticCampaignGroupContext = () => {
  const context = useContext(AnalyticCampaignGroupContext);
  if (!context) {
    throw new Error(
      'useAnalyticCampaignGroupContext must be used within a AnalyticCampaignGroupProvider',
    );
  }
  return context;
};
